import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (R8 graduation) + MULTI-ACTION (G17,
// 2026-07-10 conveyor turn). askBrain sends the message + this app's declared action
// catalog to the generic fleet chat brain (/api/apps/interview_prep_tutor/chat) — ONE
// billed call returns an ARRAY of registered actions + a reply; the app executes them
// ALL, in order ("start a nurse practice and back up" = two actions, both done).
// The regex intent-router below is the FREE FALLBACK (offline / signed-out / out of
// funds) — chat never goes dead.
//
// G16 — the chat runs the app's OWN flagship features: start_practice (the /questions
// endpoint — paste a whole job posting in the chat and go), coach_answer (the /feedback
// endpoint — the answer → feedback → edit → re-answer loop, in the chat), and the
// best-answers library, alongside search / progress / delete / backup / cloud-save.
//
// Pattern 6 — role/name resolution is FORGIVING (shared fleet fuzzy.ts): "the nusre one"
// finds the nurse session; no confident match → "did you mean …?", never a dead end.

import { BrainError, brainCall } from "../brain";
import {
  store, normalizeTag, SENIORITIES, INTERVIEW_TYPES,
  type Session, type Seniority, type InterviewType, type PracticeMode, type BestAnswer,
} from "../store";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import type { ChatAction, SessionFilter, FilterRow } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface; executeAction supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — run every one, in order
}

const HELP =
  "I can run your whole practice from here. Try:\n" +
  '• "start a senior nurse practice" — or paste a job posting and say "prepare me for this"\n' +
  '• "coach this answer: <the question> — <your answer>" and we\'ll refine it together\n' +
  '• "save that as a best answer" · "show my best answers"\n' +
  '• "show my sessions" · "how am I doing?" · "delete the product manager session"\n' +
  '• "back up my history" · "save my best answers to my GitHub"';

const none = (message: string): BrainResponse => ({ reply: "", actions: [{ kind: "none", message }] });
const one = (say: string, action: ChatAction): BrainResponse => ({ reply: say, actions: [action] });

// Pattern 6: forgiving resolution over the session roles (exact substring → fuzzy →
// "did you mean"). The stored history lives on-device, so resolution stays local.
async function findByRole(term: string): Promise<Session[]> {
  const q = term.trim().replace(/[.?!]+$/, "").replace(/\b(session|practice)\b/gi, "").trim();
  if (!q) return [];
  const all = await store.listSessions({ limit: 50000 });
  return findNamedMatches(all.map((s) => ({ ...s, name: s.role })), q);
}

function pickList(rows: Session[]): string {
  return rows.slice(0, 5).map((s) => `• ${s.role} (${s.answered_count}/${s.question_count}, ${s.created_at.slice(0, 10)})`).join("\n");
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) return routeMessage(text);
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return none(e.message);
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead.
    return routeMessage(text);
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH actions the
// message means; this file resolves references (role name → session id) locally,
// because the practice history lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.interview-prep-tutor.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

// Hard limits FIRST (the server truncates scope at 400 chars).
const SCOPE =
  "HARD LIMITS: never predict hiring outcomes or chances, never judge the person, nothing " +
  "discrimination-adjacent, no legal/visa/salary advice. Domain: job-interview practice — " +
  "coach the user's OWN answers, build and organize practice sessions and best answers, " +
  "track their progress.";

export const CHAT_ACTIONS = [
  { name: "start_practice", description: "Start a new practice session: generates tailored interview questions (one Oriora call). Use when the user asks to practice/prepare for a role OR pastes a job posting / company briefing — extract the setup from whatever they gave.", params: { role: "the role/title, enriched with company and specialty when stated, under 110 characters (e.g. 'trainee solicitor at Slaughter and May — corporate law')", seniority: "junior | mid | senior | lead", interview_type: "behavioral | technical | mixed", mode: "prep (coached, untimed — default) | simulate (timed, review at the end)", company: "optional company/organization name from their message", focus: "optional 3-6 short focus points joined with ' · '", brief: "optional: the briefing/job-posting text they pasted, verbatim" } },
  { name: "coach_answer", description: "Get structured coaching on ONE interview answer (one Oriora call): strengths, gaps, STAR note, a stronger phrasing of their own words. Use when the user gives an answer and wants feedback — including a revised answer in the refine loop.", params: { question: "the interview question being answered (from the message or the conversation)", answer: "the user's answer text, verbatim" } },
  { name: "save_best", description: "Keep an answer in the user's best-answers library. Use for 'save that answer' / 'pin this one' — pull question and answer from the conversation.", params: { question: "the question", answer: "the answer text to keep", note: "optional short note" } },
  { name: "update_best", description: "EDIT an answer already kept in the library — reword it, or change its note. Send ONLY what is changing; what you leave out is kept as it is. Use for 'change/fix/reword my saved answer about X', 'add a note to that one'. Does NOT rename the question.", params: { question: "REQUIRED — which kept answer, by its question as the user refers to it", answer: "optional — the NEW answer text", note: "optional — the NEW note" } },
  { name: "list_best", description: "Show the user's kept best answers.", params: {} },
  { name: "delete_best", description: "Remove one kept best answer, identified by its question text (the app asks the user to confirm first).", params: { question: "which best answer — its question text" } },
  { name: "filter", description: "Search / filter the user's practice-session history. All params optional; no params = list every session.", params: { text: "optional role or company text to match, e.g. \"nurse\"", seniority: "optional seniority: junior | mid | senior | lead", interview_type: "optional type: behavioral | technical | mixed", in_progress: "optional true — only sessions with unanswered questions" } },
  { name: "progress", description: "Show the user's practice progress — session and answer totals, the feedback themes flagged most, and what to work on next." },
  { name: "delete_session", description: "Delete one practice session identified by its role name from APP DATA (the app asks the user to confirm first).", params: { role: "which session (its role name)" } },
  { name: "save_to_cloud", description: "Save a note, THIS CONVERSATION, or the best-answers document to the customer's own GitHub or Google Drive (their storage; Oriora keeps nothing). Honors a named destination.", params: { source: "conversation | best_answers | text", content: "when source is text: the exact text to save", name: "optional short file name", provider: "optional: github | gdrive when the user names where" } },
  { name: "backup_save", description: "Back up the WHOLE practice history to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

// The server sends BOTH `actions` (the array) and back-compat `action`/`params` = the first
// element. Reading only the singular field silently drops work (G17) — read the array.
interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  // Metadata only — role/seniority/type/mode/counts/date + best-answer questions.
  // Never answer text or feedback bodies.
  const sessions = await store.listSessions({ limit: 40 });
  let best: BestAnswer[] = [];
  try { best = await store.listBestAnswers(); } catch { /* fresh install */ }
  const _notes = getInstructions();
  const context = ("Practice sessions (role · seniority · type · mode · answered/total · date): " +
    (sessions.length
      ? sessions.map((s) => `${s.role} (${s.seniority}, ${s.interview_type}, ${s.mode}, ${s.answered_count}/${s.question_count}, ${s.created_at.slice(0, 10)})`).join("; ")
      : "(none yet)") +
    (best.length ? "\nKept best answers (questions): " + best.slice(0, 20).map((b) => b.question).join("; ") : "")) +
    (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);

  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: "", actions: [{ kind: "none", message: say || HELP }] };   // a question, a decline, or a clarification

  const actions: ChatAction[] = [];
  const problems: string[] = [];
  for (const a of raw) {
    const res = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" });
    for (const k of res.actions) {
      if (k.kind === "none") {
        // A per-item failure ("which session?") must not silently drop the item — surface it.
        if (k.message) problems.push(k.message);
      } else {
        actions.push(k);
      }
    }
  }
  if (!actions.length) return problems.length
    ? { reply: say, actions: [{ kind: "none", message: problems.join("\n") }] }
    : { reply: say || HELP, actions: [{ kind: "none" }] };
  if (problems.length) actions.push({ kind: "none", message: problems.join("\n") });
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asBool = (v: unknown): boolean =>
  v === true || (typeof v === "string" && ["true", "yes", "1"].includes(v.trim().toLowerCase()));
const asSeniority = (v: unknown): Seniority | undefined => {
  const s = asStr(v)?.toLowerCase();
  return s && (SENIORITIES as string[]).includes(s) ? (s as Seniority) : undefined;
};
const asType = (v: unknown): InterviewType | undefined => {
  const s = asStr(v)?.toLowerCase();
  return s && (INTERVIEW_TYPES as string[]).includes(s) ? (s as InterviewType) : undefined;
};
const asMode = (v: unknown): PracticeMode => {
  const s = asStr(v)?.toLowerCase();
  return s === "simulate" ? "simulate" : "prep";
};

// Resolve a role reference to exactly one local session, or explain why not (Pattern 6:
// exact → fuzzy → offer the closest, never a dead "couldn't find").
async function resolveOne(term: string | undefined): Promise<{ row?: Session; problem?: string }> {
  if (!term) return { problem: "Which session do you mean? Give me its role name." };
  const rows = await findByRole(term);
  if (rows.length === 0) {
    const all = await store.listSessions({ limit: 50000 });
    return { problem: notFoundMessage(all.map((s) => ({ name: s.role })), term) };
  }
  if (rows.length > 1) return { problem: `I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}` };
  return { row: rows[0] };
}

export async function toAction(r: ServerChat): Promise<BrainResponse> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, actions: [{ kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) }] };
  }
  if (r.action === "library_list") return { reply: say, actions: [{ kind: "library_list", folder: asStr(p.folder) }] };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, actions: [{ kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` }] };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, actions: [{ kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` }] };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, actions: [{ kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") }] };
  }
  switch (r.action) {
    case "start_practice": {
      const role = asStr(p.role)?.slice(0, 120);
      if (!role) return none(say || 'What role should I prepare you for? e.g. "start a senior nurse practice" — or paste the job posting.');
      return one(say, {
        kind: "start_practice",
        role,
        seniority: asSeniority(p.seniority) ?? "mid",
        itype: asType(p.interview_type) ?? "mixed",
        mode: asMode(p.mode),
        company: asStr(p.company)?.slice(0, 120),
        focus: asStr(p.focus)?.slice(0, 400),
        brief: asStr(p.brief)?.slice(0, 6000),
      });
    }
    case "coach_answer": {
      const question = asStr(p.question);
      const answer = asStr(p.answer);
      if (!question || !answer) return none(say || 'Give me both — the question and your answer, e.g. "coach this answer: <question> — <your answer>".');
      return one(say, { kind: "coach_answer", question: question.slice(0, 600), answer: answer.slice(0, 8000) });
    }
    case "save_best": {
      const question = asStr(p.question);
      const answer = asStr(p.answer);
      if (!question || !answer) return none(say || "Which answer should I keep? Tell me the question and the answer (or coach one first, then say \"save that\").");
      return one(say, { kind: "save_best", question: question.slice(0, 600), answer: answer.slice(0, 8000), note: asStr(p.note)?.slice(0, 300) });
    }
    case "update_best": {
      const term = asStr(p.question);
      if (!term) return none(say || "Which kept answer should I change? Give me its question.");
      const best = await store.listBestAnswers();
      const hits = findNamedMatches(best.map((b) => ({ ...b, name: b.question })), term);
      if (hits.length === 0) return none(`I couldn't find a kept answer matching "${term}".`);
      if (hits.length > 1) return none(`I found ${hits.length} matching "${term}" — be more specific:\n${hits.slice(0, 5).map((b) => `• ${b.question}`).join("\n")}`);
      const row = hits[0];
      const nextAnswer = asStr(p.answer), nextNote = asStr(p.note);
      if (!nextAnswer && nextNote === undefined) return none(say || "What should I change — the answer, or the note?");
      // Carry the existing values through for anything not named, so a note edit cannot wipe the answer.
      return one(say, {
        kind: "update_best",
        question: row.question,
        answer: (nextAnswer ?? row.answer).slice(0, 8000),
        note: (nextNote ?? row.note ?? "").slice(0, 300),
        label: `your answer to "${row.question.slice(0, 60)}"`,
      });
    }
    case "list_best":
      return one(say, { kind: "list_best" });
    case "delete_best": {
      const term = asStr(p.question);
      if (!term) return none(say || "Which best answer should I remove? Give me its question.");
      const best = await store.listBestAnswers();
      const hits = findNamedMatches(best.map((b) => ({ ...b, name: b.question })), term);
      if (hits.length === 0) return none(notFoundMessage(best.map((b) => ({ name: b.question })), term));
      if (hits.length > 1) return none(`I found ${hits.length} matching "${term}" — be more specific:\n${hits.slice(0, 5).map((b) => `• ${b.question}`).join("\n")}`);
      return one(say, { kind: "delete_best", id: hits[0].id, label: `"${hits[0].question}"` });
    }
    case "filter": {
      const filter: SessionFilter = {};
      const text = asStr(p.text);
      if (text) filter.text = normalizeTag(text);
      const sen = asSeniority(p.seniority);
      if (sen) filter.seniority = sen;
      const ty = asType(p.interview_type);
      if (ty) filter.interview_type = ty;
      if (asBool(p.in_progress)) filter.inProgress = true;
      // An empty filter is valid here — "show my sessions" lists everything (matches the local router).
      return { reply: "", actions: [{ kind: "filter", filter, label: say || undefined }] };
    }
    case "progress":
      return one(say, { kind: "progress" });
    case "delete_session": {
      const { row, problem } = await resolveOne(asStr(p.role));
      if (!row) return none(problem!);
      return one(say, { kind: "delete_session", id: row.id, label: `the "${row.role}" session` });
    }
    case "save_to_cloud": {
      const src = asStr(p.source)?.toLowerCase();
      const source: "text" | "conversation" | "best_answers" =
        src === "conversation" ? "conversation" : src === "best_answers" || src === "best answers" ? "best_answers" : "text";
      const provider = asStr(p.provider)?.toLowerCase();
      return one(say, {
        kind: "save_to_cloud",
        source,
        content: asStr(p.content),
        name: asStr(p.name)?.slice(0, 60),
        provider: provider === "github" ? "github" : provider === "gdrive" || provider === "drive" || provider === "google drive" ? "gdrive" : undefined,
      });
    }
    case "backup_save":
      return one(say, { kind: "backup_save" });
    case "backup_status":
      return one(say, { kind: "backup_status" });
    case "backup_restore": {
      const prov = asStr(p.provider);
      return one(say, { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined });
    }
    case "save_local_copy":
      return one(say, { kind: "save_local_copy" });
    case "import_file":
      return one(say, { kind: "import_file" });
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return one(say, { kind: "remember", instruction });
    }
    default:
      return none(say || HELP);
  }
}

// ---- LOCAL INTENT-ROUTER (the free fallback) --------------------------------

export async function routeMessage(text: string): Promise<BrainResponse> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 1) BEST ANSWERS — list the kept library (local, free).
  if (/\b(best|saved|kept)\s+answers?\b/i.test(low) && !/\bdelete|remove|trash\b/i.test(low)) {
    return { reply: "", actions: [{ kind: "list_best" }] };
  }

  // 2) PROGRESS — local aggregation over the user's own history (no billed call).
  if (
    /\bhow\s+am\s+i\s+doing\b/i.test(low) ||
    /\b(my\s+)?progress\b/i.test(low) ||
    /\bkeep(s)?\s+coming\s+up\b/i.test(low) ||
    /\b(recurring|common)\s+(feedback|themes?|gaps?)\b/i.test(low) ||
    /\bam\s+i\s+(getting\s+better|improving)\b/i.test(low)
  ) {
    return { reply: "", actions: [{ kind: "progress" }] };
  }

  // 3) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove|trash)\s+(?:the\s+)?(?:session\s+|practice\s+)?(?:for\s+|called\s+)?([\s\S]+)$/i);
  if (delM) {
    const term = delM[1].trim();
    const rows = await findByRole(term);
    if (rows.length === 0) {
      const all = await store.listSessions({ limit: 50000 });
      return none(notFoundMessage(all.map((s) => ({ name: s.role })), term));
    }
    if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
    return { reply: "", actions: [{ kind: "delete_session", id: rows[0].id, label: `the "${rows[0].role}" session` }] };
  }

  // 4) FILTER / SEARCH — query-shaped → local filter over the on-device history.
  if (/\b(show|find|list|search|which|filter)\b/i.test(low)) {
    const filter = parseFilter(t);
    return { reply: "", actions: [{ kind: "filter", filter, label: t }] };
  }

  // 5) "start a practice"-shaped asks → these are billed (question generation), so the
  // free fallback can't run them; say why instead of a dead end.
  if (/\b(start|new|begin|practice|prepare|question)\b/i.test(low)) {
    return none("Starting a practice needs the AI (one Oriora call) and I can't reach it right now — check your connection or sign-in, then ask again, or use \"Start a practice\" on the main screen.");
  }

  // 6) Unclear → a short clarifying nudge.
  return none(HELP);
}

/** Build a local SessionFilter from free text — seniority, type, in-progress, role text. */
function parseFilter(text: string): SessionFilter {
  const low = text.toLowerCase();
  const f: SessionFilter = {};
  if (/\b(unfinished|in\s*progress|incomplete|open)\b/i.test(low)) f.inProgress = true;
  for (const s of SENIORITIES) {
    if (new RegExp(`\\b${s}\\b`, "i").test(low)) { f.seniority = s; break; }
  }
  if (!f.seniority && /\blead\b|\bmanager\b/i.test(low)) f.seniority = "lead";
  for (const ty of INTERVIEW_TYPES) {
    if (new RegExp(`\\b${ty}\\b`, "i").test(low)) { f.interview_type = ty; break; }
  }
  // Role text: "find/show ... <term> (session|sessions|one)"
  const m = text.match(/\b(?:find|show|search)\s+(?:me\s+)?(?:the\s+)?(.+?)(?:\s+(?:sessions?|practices?|one))?\s*[.?!]*$/i);
  if (m) {
    const term = normalizeTag(m[1])
      .replace(/\b(my|all|sessions?|practices?|list|of)\b/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (term && !SENIORITIES.includes(term as never) && !INTERVIEW_TYPES.includes(term as never)) f.text = term;
  }
  return f;
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
